import QuickComposeAttendeeRows from './QuickComposeAttendeeRows';
import QuickComposeCollabSpaceRow from './QuickComposeCollabSpaceRow';
import QuickComposeLocationWell from './QuickComposeLocationWell';
import RecurrenceComponent from './RecurrenceComponent';
import TimeComponent from './TimeComponent';
import TitleAndSuggestionsWithCharm from './TitleAndSuggestionsWithCharm';
import getComposeStyles from '../utils/getComposeStyles';
import isOofPivotActive from '../utils/isOofPivotActive';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { observer as observer_0 } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { CalendarChannelPicker } from 'owa-calendar-channel-picker';
import { lazyShowFullCompose } from 'owa-calendar-compose-form-store';
import type { ComposeFormStore } from 'owa-calendar-compose-types';
import getQuickComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getQuickComposeViewState';
import isNewEvent from 'owa-calendar-compose-form-store/lib/selectors/isNewEvent';
import isQuickComposeInEditMode from 'owa-calendar-compose-form-store/lib/selectors/isQuickComposeInEditMode';
import canChangeCalendar from 'owa-calendar-compose-form-store/lib/utils/canChangeCalendar';
import { isEventInstanceOfSeries, isOnGroupCalendar } from 'owa-calendar-event-capabilities';
import { isCssVariablesFeatureEnabled } from 'owa-calendar-feature-flags';
import AllDayToggle from 'owa-calendar-forms-common/lib/components/AllDayToggle';
import getGroupAsAttendee from 'owa-calendar-forms-common/lib/utils/getGroupAsAttendee';
import {
    FULL_COMPOSE_VIEW_TYPE_MODAL,
    CharmSelectionType,
} from 'owa-calendar-helpers-types/lib/ComposeTypes';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { ControlIcons } from 'owa-control-icons';
import { EmailReminderEntrySource, ReminderDropdown } from 'owa-email-reminders';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { invitationWontBeSentToGroup } from 'owa-locstrings/lib/strings/invitationwontbesenttogroup.locstring.json';
import { sendEventToAllGroupMembers } from 'owa-locstrings/lib/strings/sendeventtoallgroupmembers.locstring.json';
import { lazyCanTravelToEvent, MobilityFullComposeRow } from 'owa-mobility-compose';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import * as React from 'react';
import { lazyModule } from 'owa-calendar-scheduling-assistant';
import {
    FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
    QuickComposeControl,
} from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import {
    onCalendarIdChanged,
    onChannelChanged,
    onRequiredAttendeesChanged,
    logQuickComposeControlUsed,
    onReminderChanged,
    updateAllDay,
    setRecurrence,
    openComposeSchedulingAssistant,
} from 'owa-calendar-compose-form-actions';
import { LazyQuickComposeEditor } from '../contentsIndex';
import type { CreateCollabSpaceOnInitProps } from 'owa-calendar-helpers-types';
import type { IButtonProps } from '@fluentui/react/lib/Button';
import CalendarBarButtons from 'owa-calendar-forms-common/lib/components/CalendarBarButtons';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

const styles = require('./QuickComposeContent.scss');
const classNames = require('classnames/bind').bind(styles);

/**
 * Props for quick compose content
 */
export interface QuickComposeContentProps {
    state: ComposeFormStore;
    colorScheme: ColorScheme;
    calendarBarButtonProps: IButtonProps[];
    onSizeUpdate: () => void;
    onLaunchFullComposeCallback?: (
        createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps
    ) => void;
}

/**
 * Component for rendering quick compose
 */
export default observer_0(function QuickComposeContent(props: QuickComposeContentProps) {
    React.useEffect(props.onSizeUpdate);

    /**
     * Adds the group as an attendee to the event and Opens full compose
     */
    const addGroupAttendeeAndOpenFullCompose = e => {
        let { state } = props;
        logUsage('inviteGroupMembersClicked', ['QuickCompose']);
        let mergedAttendees: AttendeeType[] = [];
        mergedAttendees.push(...state.event.RequiredAttendees);
        mergedAttendees.push(getGroupAsAttendee(state.event));

        onRequiredAttendeesChanged(mergedAttendees);
        if (props.onLaunchFullComposeCallback) {
            props.onLaunchFullComposeCallback();
        } else {
            launchFullCompose();
        }
    };
    /**
     * Returns true if the form is in edit mode and the item being edited
     * is an instance of a series.
     */
    const isInEditSeriesInstanceMode = (): boolean => {
        let { state } = props;
        return isQuickComposeInEditMode() && isEventInstanceOfSeries(state.event);
    };
    const onRecurrenceChanged = (newRecurrence: RecurrenceType): void => {
        logQuickComposeControlUsed(QuickComposeControl.Recurrence, true);
        setRecurrence(newRecurrence);
    };
    const { state, colorScheme, onSizeUpdate, calendarBarButtonProps } = props;
    const storeViewState = getQuickComposeViewState();

    // Check prop.state.event before going any further.
    // Even though we check in QuickComposeRenderer that viewState.showQuickCompose is true,
    // our component's render could still be invoked with event and originalEvent being null.
    // This would typically happen right after a save, when state.event props are updated with
    // finalized fix ups (e.g. setFriendlyToFinalizedDate).
    //
    // With react 16 we can also be called when storeViewState is torn down.
    if (!state.event || !storeViewState) {
        return null;
    }
    const isInEditMode = isQuickComposeInEditMode();
    const { ReminderMinutesBeforeStart, ReminderIsSet } = state.event;
    const selectedReminderTimeInMinutes = ReminderIsSet ? ReminderMinutesBeforeStart : -1;
    const isBasicCompose = !isInEditMode;
    const calendarTheme = themeFromColorScheme(colorScheme);
    // This function is to support cal-css-variables feature flag and generate the expected style
    const composeGeneratedStyles = getComposeStyles(
        !isCssVariablesFeatureEnabled(),
        colorScheme,
        styles.useCssVariables,
        styles.editTitleTextInputCssVariables
    );
    // Only show recipient well for new events by enterprise users
    const showRecipientWell: boolean = !isConsumer() && !isInEditMode;
    const showRecipientWellAvailability: boolean = showRecipientWell && !state.event.Recurrence;
    const isTimePanelViewType = storeViewState.viewType == 'TimePanel';
    const showCalendarChannelPicker = !isOofPivotActive();
    const calendarPickerCalloutStyle = isTimePanelViewType
        ? styles.calendarChannelPickerCalloutTimePanel
        : isInEditMode
        ? styles.calendarChannelPickerCalloutEditPeek
        : null;
    let showMobilityCompose = false;
    if (isFeatureEnabled('cal-mf-mobility')) {
        const canTravelToEvent = lazyCanTravelToEvent.tryImportForRender();

        if (canTravelToEvent) {
            showMobilityCompose = canTravelToEvent(state.event);
        }
    }
    const isBarExp =
        isHostAppFeatureEnabled('reduceCalBarExp') &&
        isFeatureEnabled('cal-mf-reduceCalendarBar') &&
        !isTimePanelViewType;
    return (
        <div
            className={classNames(
                isBasicCompose ? styles.basicQuickComposeContainer : styles.composeContainer,
                {
                    isCalendarPickerAboveTitle: showCalendarChannelPicker && isTimePanelViewType,
                    [styles.quickComposeContainerExp]: isBarExp,
                }
            )}>
            <div className={classNames({ [styles.navigationRow]: isBarExp })}>
                {showCalendarChannelPicker && (
                    <div
                        className={classNames(
                            styles.composeRow,
                            styles.calendarChannelPickerContainer,
                            { [styles.calendarPickerBar]: isBarExp }
                        )}>
                        <CalendarChannelPicker
                            event={state.event}
                            colorScheme={colorScheme}
                            readonly={!canChangeCalendar()}
                            styles={{
                                textField: styles.calendarChannelPickerTextField,
                                textFieldRoot: !isTimePanelViewType
                                    ? isInEditMode
                                        ? styles.calendarPickerTextFieldRootEditMode
                                        : styles.calendarPickerTextFieldRoot
                                    : styles.calendarPickerTextFieldRootTimePanel,
                                callout: calendarPickerCalloutStyle,
                                optionsContainer: !isTimePanelViewType
                                    ? styles.calendarPickerOptionsContainerPeek
                                    : null,
                            }}
                            onCalendarIdChanged={onCalendarIdChanged}
                            onChannelChanged={onChannelChanged}
                        />
                    </div>
                )}
                {isBarExp && (
                    <CalendarBarButtons
                        className={styles.navigationButtons}
                        colorScheme={colorScheme}
                        buttonProps={calendarBarButtonProps}
                    />
                )}
            </div>
            <div
                className={classNames(
                    isInEditMode ? styles.editTitleContainer : styles.titleContainer,
                    { smallBottomMargin: showRecipientWell }
                )}>
                <TitleAndSuggestionsWithCharm
                    colorScheme={colorScheme}
                    isInEditMode={isInEditMode}
                />
                <AllDayToggle
                    isAllDay={state.event.IsAllDayEvent}
                    onAllDayToggled={onAllDayToggled}
                    colorScheme={colorScheme}
                />
            </div>
            {showRecipientWell && (
                <QuickComposeAttendeeRows
                    colorScheme={colorScheme}
                    theme={calendarTheme}
                    showAvailability={showRecipientWellAvailability}
                />
            )}
            <TimeComponent
                isInEditMode={isInEditMode}
                colorScheme={colorScheme}
                event={state.event}
                isTimePanelViewType={isTimePanelViewType}
                onSizeUpdate={onSizeUpdate}
                onTimeSuggestionNotFound={onTimeSuggestionNotFound}
            />
            <div className={classNames(styles.composeRow, styles.locationComposeRow)}>
                <Icon iconName={ControlIcons.POI} className={classNames(styles.locationRowIcon)} />
                <QuickComposeLocationWell isNarrow={isInEditMode} />
            </div>
            {showMobilityCompose && (
                <div className={classNames(styles.composeRow, styles.mobilityComposeRow)}>
                    <MobilityFullComposeRow event={state.event} entrySource={'QuickCompose_Edit'} />
                </div>
            )}
            {!isInEditSeriesInstanceMode() && (
                <div className={classNames(styles.composeRow, styles.recurrenceContainer)}>
                    <Icon
                        iconName={ControlIcons.SyncOccurence}
                        className={styles.recurrenceRowIcon}
                    />
                    <RecurrenceComponent
                        colorScheme={colorScheme}
                        className={styles.recurrenceComponent}
                        event={state.event}
                        onRecurrenceChangedCallback={onRecurrenceChanged}
                        cssVariablesStyle={composeGeneratedStyles}
                        onSizeUpdate={onSizeUpdate}
                        isNewEvent={isNewEvent()}
                    />
                </div>
            )}
            <div className={classNames(styles.composeRow, styles.reminderContainer)}>
                <Icon iconName={ControlIcons.AlarmClock} className={styles.reminderRowIcon} />
                <ReminderDropdown
                    event={state.event}
                    colorScheme={colorScheme}
                    entrySource={EmailReminderEntrySource.QuickCompose}
                    onReminderChanged={onReminderChanged_0}
                    selectedReminderTimeInMinutes={selectedReminderTimeInMinutes}
                />
            </div>
            {!isInEditMode && <LazyQuickComposeEditor colorScheme={colorScheme} />}
            <QuickComposeCollabSpaceRow
                event={state.event}
                composeStyles={composeGeneratedStyles}
                launchFullCompose={props.onLaunchFullComposeCallback ?? launchFullCompose}
            />
            {isOnGroupCalendar(state.event) && (
                <div className={classNames(styles.composeRow, styles.infoBarRowContainer)}>
                    <Icon iconName={ControlIcons.Info} className={styles.infoBarRowIcon} />
                    <div className={styles.infoBarMessageContainer}>
                        {loc(invitationWontBeSentToGroup)}
                        <Link
                            styles={composeGeneratedStyles.linkGetStyles}
                            onClick={addGroupAttendeeAndOpenFullCompose}
                            className={classNames(
                                styles.infoBarMessageLine,
                                composeGeneratedStyles.cssVariabelsStyle
                            )}>
                            {loc(sendEventToAllGroupMembers)}
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
});

/**
 * Event handler when more details link is clicked to open full compose
 */
function launchFullCompose(createCollabSpaceOnInitProps?: CreateCollabSpaceOnInitProps) {
    const storeViewState = getQuickComposeViewState();
    lazyShowFullCompose.importAndExecute(
        storeViewState.charmSelection == CharmSelectionType.Manual,
        FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
        FULL_COMPOSE_VIEW_TYPE_MODAL,
        undefined /* extractionSourceId */,
        undefined /* internetMessageId */,
        createCollabSpaceOnInitProps
    );
}

/**
 * onAllDayToggled handler
 */
function onAllDayToggled(isChecked) {
    logQuickComposeControlUsed(QuickComposeControl.AllDay, true /* isFocus */);
    updateAllDay(isChecked, true /* isAllDayEventExplicitlySet */);
}

function onReminderChanged_0(reminderInMinutes: number): void {
    logQuickComposeControlUsed(QuickComposeControl.Reminder, true /* isFocus */);
    onReminderChanged(reminderInMinutes);
}

/**
 * Callback handler when time suggestion is not found
 */
async function onTimeSuggestionNotFound() {
    const storeViewState = getQuickComposeViewState();
    // Await scheduling assistant package import before showing compose
    // to ensure compose  is not displayed first while scheduling assistant loads
    await lazyModule.import();
    await lazyShowFullCompose.importAndExecute(
        storeViewState.charmSelection == CharmSelectionType.Manual,
        FULL_COMPOSE_ENTRY_SOURCE_QUICK_COMPOSE,
        FULL_COMPOSE_VIEW_TYPE_MODAL
    );
    openComposeSchedulingAssistant(null /* formId */);
}
